/**
 * Builds the grouped-by-office meeting-room options used by the Details
 * form's Meeting Room select (Figma recipe — see
 * `CustomRenderOptionWithGroups[Multi]` in
 * `components/packages/core/src/components/molecules/Select/SelectVariations.stories.tsx`).
 *
 * - Each group corresponds to `room.office_name` (unknown rooms land in
 *   a generic "Other" bucket).
 * - Each option carries `data: { status, capacity }` so render props
 *   can paint the Available/Busy tag and the person-count footer.
 *
 * Fetching is done via the existing `useMeetingRooms` trigger + poll
 * hook. We refetch whenever the time window materially changes.
 */
import { useEffect, useMemo } from 'react';
import moment from 'moment-timezone';
import { useMeetingRooms } from '@/api/useMeetingRooms';
import type { MeetingRoomGroup, MeetingRoomOptionData } from '@/formConfigs';

const OTHER_GROUP_LABEL = 'Other';

export interface UseMeetingRoomGroupedOptionsArgs {
  timezone: string;
  /** Preferred window — booked slot start/end in ISO. Falls back to
   *  today's 09:00–18:00 in `timezone` when no slot is picked yet. */
  slotStartIso?: string | null;
  slotEndIso?: string | null;
  /** Gate the fetch (e.g. only when the user has opened the room
   *  section). Defaults to true. */
  enabled?: boolean;
  /**
   * Reschedule self-booking exclusion (edit mode only). The BE
   * `/meeting_rooms` availability worker reports a room "busy" off raw
   * free/busy, with no knowledge of which interview is being edited —
   * so a room this interview already holds shows "Busy" purely because
   * of its own booking. The BE can't be told to exclude it (the fix is
   * tracked there), so we override the badge to "available" for exactly
   * the rooms THIS interview booked, and only while the queried window
   * still overlaps the original booked slot (the only window in which
   * the self-booking can be the cause of the busy). Outside that — any
   * other room, a moved-to-non-overlapping time, or create mode — the
   * BE status is used verbatim, so a genuinely busy room still reads
   * "Busy".
   */
  isEditReschedule?: boolean;
  /** Emails of the rooms this interview already booked (raw GET
   *  `meeting_rooms[].email`). Only these are eligible for the override. */
  selfBookedRoomEmails?: string[];
  /** Original booked slot (raw GET `start_time`/`end_time`). The override
   *  applies only while the queried window overlaps this. */
  originalSlotStartIso?: string | null;
  originalSlotEndIso?: string | null;
}

/** Absolute-instant overlap of two ISO intervals. Returns false on any
 *  missing/invalid bound so the caller defaults to "no override". */
function isoIntervalsOverlap(
  aStart: string | null | undefined,
  aEnd: string | null | undefined,
  bStart: string | null | undefined,
  bEnd: string | null | undefined
): boolean {
  if (!aStart || !aEnd || !bStart || !bEnd) return false;
  const as = new Date(aStart).getTime();
  const ae = new Date(aEnd).getTime();
  const bs = new Date(bStart).getTime();
  const be = new Date(bEnd).getTime();
  if ([as, ae, bs, be].some((n) => Number.isNaN(n))) return false;
  return as < be && bs < ae;
}

export interface UseMeetingRoomGroupedOptionsResult {
  groups: MeetingRoomGroup[];
  /** `email → displayName` map — lets the calendar swap the raw
   *  meeting subject for a friendly room label on busy events. */
  namesByEmail: Record<string, string>;
  isLoading: boolean;
  error: Error | null;
}

export function useMeetingRoomGroupedOptions({
  timezone,
  slotStartIso,
  slotEndIso,
  enabled = true,
  isEditReschedule = false,
  selfBookedRoomEmails,
  originalSlotStartIso,
  originalSlotEndIso,
}: UseMeetingRoomGroupedOptionsArgs): UseMeetingRoomGroupedOptionsResult {
  const { rooms, isLoading, error, fetchRooms, cleanup } = useMeetingRooms();

  // Normalise the time window. Without a booked slot we ask for the
  // first reasonable future slot so rooms still show Available/Busy.
  const { startIso, endIso } = useMemo(() => {
    if (slotStartIso && slotEndIso) {
      return { startIso: slotStartIso, endIso: slotEndIso };
    }
    const start = moment().tz(timezone).startOf('day').add(9, 'hours');
    const end = start.clone().add(9, 'hours');
    return { startIso: start.toISOString(), endIso: end.toISOString() };
  }, [slotStartIso, slotEndIso, timezone]);

  useEffect(() => {
    if (!enabled) return;
    fetchRooms({ startTime: startIso, endTime: endIso, timezone });
    return () => {
      cleanup();
    };
    // cleanup ref is stable from the hook — exhaustive-deps noisy otherwise
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, startIso, endIso, timezone]);

  // Self-booking masks "busy" only when we're editing AND the window we
  // queried still overlaps the interview's original slot — see the
  // `isEditReschedule` arg doc. Rooms not in this set are never overridden.
  const selfBookedSet = useMemo(
    () =>
      new Set(
        (selfBookedRoomEmails ?? []).filter(
          (e): e is string => typeof e === 'string' && e.length > 0
        )
      ),
    [selfBookedRoomEmails]
  );
  const overrideSelfBookedBusy = useMemo(
    () =>
      isEditReschedule &&
      selfBookedSet.size > 0 &&
      isoIntervalsOverlap(
        startIso,
        endIso,
        originalSlotStartIso,
        originalSlotEndIso
      ),
    [
      isEditReschedule,
      selfBookedSet,
      startIso,
      endIso,
      originalSlotStartIso,
      originalSlotEndIso,
    ]
  );

  const { groups, namesByEmail } = useMemo(() => {
    if (!rooms.length) {
      return {
        groups: [] as MeetingRoomGroup[],
        namesByEmail: {} as Record<string, string>,
      };
    }
    const byOffice = new Map<string, MeetingRoomGroup>();
    const names: Record<string, string> = {};
    for (const room of rooms) {
      if (!room.email) continue;
      names[room.email] = room.name;
      const officeName = room.office_name?.trim() || OTHER_GROUP_LABEL;
      let group = byOffice.get(officeName);
      if (!group) {
        group = { label: officeName, options: [] };
        byOffice.set(officeName, group);
      }
      // BE busy that's explained solely by this interview's own booking
      // reads as available during a reschedule (overlapping window).
      const busyFromBe = room.available === false;
      const isSelfBooked =
        overrideSelfBookedBusy && selfBookedSet.has(room.email);
      const data: MeetingRoomOptionData = {
        status: busyFromBe && !isSelfBooked ? 'busy' : 'available',
        capacity: typeof room.capacity === 'number' ? room.capacity : undefined,
      };
      group.options.push({
        value: room.email,
        label: room.name,
        data,
      });
    }
    const sortedGroups = Array.from(byOffice.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
    return { groups: sortedGroups, namesByEmail: names };
  }, [rooms, overrideSelfBookedBusy, selfBookedSet]);

  return { groups, namesByEmail, isLoading, error };
}
